/**
 * EHR Provider Metadata
 *
 * Provides display names, operating countries, and classification
 * for all EHR providers. Used by the integrations UI to show
 * user-friendly names and sort by user's country.
 */

// Import the leaf module directly rather than the @/features/flags barrel
import { IS_GDT_INTEGRATION_ENABLED } from '@/features/flags/legacy-flags';
import { posthog } from '@/features/flags/posthog-compat';
import { getDuriaDefaultGdtSectionMappings } from '@/integrations/gdt/pvs/duria';
import { getMedatixxDefaultGdtSectionMappings } from '@/integrations/gdt/pvs/medatixx';
import { getMedicalOfficeDefaultGdtSectionMappings } from '@/integrations/gdt/pvs/medical-office';
import { X_ISYNET_PVS_SYSTEM_NAME } from '@/integrations/gdt/pvs/x-isynet';
import { T2MED_FEATURE_FLAG } from '@/integrations/t2med/types';
import { EHRProvider } from '@heidi/schema/ml-scribe';

/**
 * Country code to display name mapping
 * Includes: OECD, EU, Asia, Africa, South America, North America
 */
export const COUNTRY_NAMES: Record<string, string> = {
  // North America
  US: 'United States',
  CA: 'Canada',
  MX: 'Mexico',

  // Europe - Western
  GB: 'United Kingdom',
  IE: 'Ireland',
  FR: 'France',
  DE: 'Germany',
  AT: 'Austria',
  CH: 'Switzerland',
  BE: 'Belgium',
  NL: 'Netherlands',
  LU: 'Luxembourg',

  // Europe - Northern
  DK: 'Denmark',
  SE: 'Sweden',
  NO: 'Norway',
  FI: 'Finland',
  IS: 'Iceland',
  EE: 'Estonia',
  LV: 'Latvia',
  LT: 'Lithuania',

  // Europe - Southern
  ES: 'Spain',
  PT: 'Portugal',
  IT: 'Italy',
  GR: 'Greece',
  MT: 'Malta',
  CY: 'Cyprus',

  // Europe - Central & Eastern
  PL: 'Poland',
  CZ: 'Czech Republic',
  SK: 'Slovakia',
  HU: 'Hungary',
  SI: 'Slovenia',
  HR: 'Croatia',
  RO: 'Romania',
  BG: 'Bulgaria',
  TR: 'Turkey',

  // Oceania
  AU: 'Australia',
  NZ: 'New Zealand',

  // Asia
  JP: 'Japan',
  KR: 'South Korea',
  CN: 'China',
  HK: 'Hong Kong',
  TW: 'Taiwan',
  SG: 'Singapore',
  MY: 'Malaysia',
  TH: 'Thailand',
  ID: 'Indonesia',
  PH: 'Philippines',
  VN: 'Vietnam',
  IN: 'India',

  // Middle East
  IL: 'Israel',
  AE: 'United Arab Emirates',
  SA: 'Saudi Arabia',

  // Africa
  ZA: 'South Africa',

  // South America
  BR: 'Brazil',
  AR: 'Argentina',
  CL: 'Chile',
  CO: 'Colombia',
  PE: 'Peru',
  VE: 'Venezuela',
  EC: 'Ecuador',
  UY: 'Uruguay',
  PY: 'Paraguay',
  BO: 'Bolivia',

  // Central America & Caribbean
  CR: 'Costa Rica',
  PA: 'Panama',
};

/**
 * Country code to flag emoji mapping
 * Includes: OECD, EU, Asia, Africa, South America, North America
 */
export const COUNTRY_FLAGS: Record<string, string> = {
  // North America
  US: '🇺🇸',
  CA: '🇨🇦',
  MX: '🇲🇽',

  // Europe - Western
  GB: '🇬🇧',
  IE: '🇮🇪',
  FR: '🇫🇷',
  DE: '🇩🇪',
  AT: '🇦🇹',
  CH: '🇨🇭',
  BE: '🇧🇪',
  NL: '🇳🇱',
  LU: '🇱🇺',

  // Europe - Northern
  DK: '🇩🇰',
  SE: '🇸🇪',
  NO: '🇳🇴',
  FI: '🇫🇮',
  IS: '🇮🇸',
  EE: '🇪🇪',
  LV: '🇱🇻',
  LT: '🇱🇹',

  // Europe - Southern
  ES: '🇪🇸',
  PT: '🇵🇹',
  IT: '🇮🇹',
  GR: '🇬🇷',
  MT: '🇲🇹',
  CY: '🇨🇾',

  // Europe - Central & Eastern
  PL: '🇵🇱',
  CZ: '🇨🇿',
  SK: '🇸🇰',
  HU: '🇭🇺',
  SI: '🇸🇮',
  HR: '🇭🇷',
  RO: '🇷🇴',
  BG: '🇧🇬',
  TR: '🇹🇷',

  // Oceania
  AU: '🇦🇺',
  NZ: '🇳🇿',

  // Asia
  JP: '🇯🇵',
  KR: '🇰🇷',
  CN: '🇨🇳',
  HK: '🇭🇰',
  TW: '🇹🇼',
  SG: '🇸🇬',
  MY: '🇲🇾',
  TH: '🇹🇭',
  ID: '🇮🇩',
  PH: '🇵🇭',
  VN: '🇻🇳',
  IN: '🇮🇳',

  // Middle East
  IL: '🇮🇱',
  AE: '🇦🇪',
  SA: '🇸🇦',

  // Africa
  ZA: '🇿🇦',

  // South America
  BR: '🇧🇷',
  AR: '🇦🇷',
  CL: '🇨🇱',
  CO: '🇨🇴',
  PE: '🇵🇪',
  VE: '🇻🇪',
  EC: '🇪🇨',
  UY: '🇺🇾',
  PY: '🇵🇾',
  BO: '🇧🇴',

  // Central America & Caribbean
  CR: '🇨🇷',
  PA: '🇵🇦',
};

/** Standard GDT 6330 section mappings (German-only) — AN/BE/TH/PR accepted across all German PVS systems. */
const STANDARD_GDT_SECTION_MAPPINGS = [
  { fieldId: '6330', sectionName: 'Anamnese', categoryCode: 'AN' },
  { fieldId: '6330', sectionName: 'Befund', categoryCode: 'BE' },
  { fieldId: '6330', sectionName: 'Therapie', categoryCode: 'TH' },
  { fieldId: '6330', sectionName: 'Procedere', categoryCode: 'PR' },
] as const;

export interface EhrProviderMetadata {
  /** User-friendly display name (Title Case unless acronym) */
  displayName: string;
  /**
   * Array of ISO 2-letter country codes where this system operates.
   * If empty or undefined, system is considered truly global (all countries).
   */
  operatingCountries?: string[];
  /** Whether this EMR supports GDT integration (German file protocol) */
  supportsGdt?: boolean;
  /** PVS system name for GDT (maps to known PVS systems in backend) */
  pvsSystemName?: string;
  /** Default GDT-ID for this PVS (max 8 chars) */
  pvsGdtId?: string;
  /** Whether this PVS requires a separate PDF folder (vs embedding in GDT) */
  requiresPdfFolder?: boolean;
  /** Whether this PVS requires a fixed output filename (vs unique timestamped names) */
  requiresFixedFilename?: boolean;
  /** Default input filename PVS writes to and Heidi reads from */
  defaultInputFilename?: string;
  /** Default output filename Heidi writes to and PVS reads from */
  defaultOutputFilename?: string;
  /** Whether this PVS is macOS-only */
  isMacOSOnly?: boolean;
  /**
   * Whether this PVS accepts Heidi's structured `6330`/`6331` section output.
   * Defaults to true for GDT providers. Set to false when the tested return
   * template only consumes flat `6220` result fields.
   */
  supportsGdtSectionalNotes?: boolean;
  /**
   * Default GDT section-to-category mappings for this PVS.
   * Pre-populates the section mapping in the GDT setup UI when the mapping is empty.
   * Each entry maps a section name (matched case-insensitively) to a GDT category
   * field identifier and category code.
   */
  defaultGdtSectionMappings?: Array<{
    sectionName: string;
    categoryCode: string;
    fieldId?: string;
  }>;
  /**
   * Whether this PVS reads section content by GDT field identifier rather than by
   * a 6330 letter code (Duria's `Feldkennung,Textgruppe` import model).
   *
   * When true, the writer emits each section's content directly under `fieldId`.
   * Pre-fills `sectionsAsFieldIds` in the GDT config for this provider. Defaults
   * to false.
   */
  gdtSectionsAsFieldIds?: boolean;
  /**
   * Whether users may override category/content pair routing and write sections
   * directly under configured GDT field identifiers.
   *
   * Defaults to true. Set to false when the PVS importer requires paired
   * category/content fields and cannot consume direct field mappings.
   */
  allowsGdtDirectFieldMapping?: boolean;
  /**
   * Whether enabling sectional notes requires a compatible external PVS template.
   *
   * The shared GDT setup UI uses this capability to show an inline warning while
   * sectional notes are enabled. Defaults to false.
   */
  requiresGdtSectionalNotesTplSupport?: boolean;
  /**
   * Whether this PVS requires the section heading written as the first 6331 content line.
   * Defaults to false — the 6330 category code serves as the section label for standard
   * GDT-compliant systems. Set to true only for PVS systems confirmed to need the heading
   * as human-readable text in addition to the 6330 code.
   */
  requiresSectionHeadingInContent?: boolean;
}

/**
 * Test PVS GDT ID that triggers test mode with folder configuration
 * When user enters this as their PVS GDT ID, folder pickers are shown
 * Must be 8 characters or less per GDT spec
 */
export const GDT_TEST_MODE_ID = 'TESTGDT';

/**
 * Metadata for all EHR providers
 * Display names follow Title Case unless the name is an acronym (e.g., CGM, EMIS)
 */
export const EHR_PROVIDER_METADATA: Partial<Record<EHRProvider, EhrProviderMetadata>> = {
  // ==================== German Systems ====================
  [EHRProvider.ALBIS]: {
    displayName: 'CGM Albis',
    operatingCountries: ['DE'],
    supportsGdt: true,
    pvsSystemName: 'CGM ALBIS',
    pvsGdtId: 'ALBIS01',
    requiresFixedFilename: true,
    defaultInputFilename: 'albis2heidi.gdt',
    defaultOutputFilename: 'heidi2albis.gdt',
  },
  [EHRProvider.Anderes_System]: {
    displayName: 'Anderes System',
    operatingCountries: ['DE', 'AT', 'CH'],
    supportsGdt: true,
    pvsSystemName: 'Other / Custom',
  },
  [EHRProvider.ARZT_2000]: {
    displayName: 'Arzt 2000',
    operatingCountries: ['DE'],
    supportsGdt: true,
    pvsSystemName: 'Other / Custom',
  },
  [EHRProvider.CGM_CLINICAL]: {
    displayName: 'CGM Clinical',
    operatingCountries: ['DE', 'AT'],
    supportsGdt: true,
    pvsSystemName: 'Other / Custom',
  },
  [EHRProvider.CGM_M1_PRO]: {
    displayName: 'CGM M1 Pro',
    operatingCountries: ['DE', 'AT'],
    supportsGdt: true,
    pvsSystemName: 'CGM M1 PRO',
    pvsGdtId: 'M1PRO01',
  },
  [EHRProvider.CGM_MEDICO]: {
    displayName: 'CGM Medico',
    operatingCountries: ['DE'],
    supportsGdt: true,
    pvsSystemName: 'Other / Custom',
  },
  [EHRProvider.CGM_MEDISTAR]: {
    displayName: 'CGM Medistar',
    operatingCountries: ['DE'],
    supportsGdt: true,
    pvsSystemName: 'Other / Custom',
  },
  [EHRProvider.Data_AL]: {
    displayName: 'Data-AL',
    operatingCountries: ['DE'],
    supportsGdt: true,
    pvsSystemName: 'Other / Custom',
  },
  [EHRProvider.Doctorly]: { displayName: 'Doctorly', operatingCountries: ['DE'] },
  [EHRProvider.DURIA]: {
    displayName: 'Duria',
    operatingCountries: ['DE'],
    supportsGdt: true,
    pvsSystemName: 'Duria',
    pvsGdtId: 'DURIA01',
    requiresFixedFilename: true,
    defaultInputFilename: 'duria2heidi.gdt',
    defaultOutputFilename: 'heidi2duria.gdt',
    gdtSectionsAsFieldIds: true,
    defaultGdtSectionMappings: getDuriaDefaultGdtSectionMappings(),
  },
  [EHRProvider.DURIA2]: {
    displayName: 'Duria2',
    operatingCountries: ['DE'],
    supportsGdt: true,
    pvsSystemName: 'Duria2',
    pvsGdtId: 'DURIA02',
    requiresFixedFilename: true,
    defaultInputFilename: 'duria2heidi.gdt',
    defaultOutputFilename: 'heidi2duria.gdt',
    gdtSectionsAsFieldIds: true,
    defaultGdtSectionMappings: getDuriaDefaultGdtSectionMappings(),
  },
  [EHRProvider.Elefant]: {
    displayName: 'Elefant',
    operatingCountries: ['DE'],
    supportsGdt: true,
    pvsSystemName: 'Other / Custom',
  },
  [EHRProvider.Epikur]: {
    displayName: 'Epikur',
    operatingCountries: ['DE'],
    supportsGdt: true,
    pvsSystemName: 'Other / Custom',
  },
  [EHRProvider.esQlab_online]: {
    displayName: 'esQlab.online',
    operatingCountries: ['DE'],
  },
  [EHRProvider.EVA]: {
    displayName: 'EVA',
    operatingCountries: ['DE'],
    supportsGdt: true,
    pvsSystemName: 'Other / Custom',
  },
  [EHRProvider.EVIDENT]: {
    displayName: 'Evident',
    operatingCountries: ['DE'],
    supportsGdt: true,
    pvsSystemName: 'Other / Custom',
  },
  [EHRProvider.FIDUS]: {
    displayName: 'Fidus',
    operatingCountries: ['DE'],
    supportsGdt: true,
    pvsSystemName: 'Other / Custom',
  },
  [EHRProvider.Genius]: {
    displayName: 'Genius',
    operatingCountries: ['DE'],
    supportsGdt: true,
    pvsSystemName: 'Other / Custom',
  },
  [EHRProvider.IMedOne]: { displayName: 'iMedOne', operatingCountries: ['DE'] },
  [EHRProvider.IMP_System]: { displayName: 'IMP::System', operatingCountries: ['DE'] },
  [EHRProvider.inSuite]: {
    displayName: 'Doc Cirrus inSuite',
    operatingCountries: ['DE'],
    supportsGdt: true,
    pvsSystemName: 'Doc Cirrus inSuite',
    pvsGdtId: 'INSUI01',
  },
  [EHRProvider.InterARZT]: { displayName: 'InterArzt', operatingCountries: ['DE'] },
  [EHRProvider.KiWi]: {
    displayName: 'KiWi',
    operatingCountries: ['DE'],
    supportsGdt: true,
    pvsSystemName: 'Other / Custom',
  },
  [EHRProvider.M_KIS]: { displayName: 'M-KIS', operatingCountries: ['DE'] },
  // Canonical Medatixx PVS names for TS/Rust detection live in these
  // pvsSystemName values. Keep `src/integrations/gdt/pvs/medatixx.ts` and
  // `tauri-plugin/integrations/tauri-plugin-gdt/src/pvs/medatixx.rs` in sync.
  [EHRProvider.medatixx]: {
    displayName: 'medatixx',
    operatingCountries: ['DE'],
    supportsGdt: true,
    pvsSystemName: 'medatixx',
    defaultGdtSectionMappings: getMedatixxDefaultGdtSectionMappings(),
    requiresSectionHeadingInContent: true,
  },
  [EHRProvider.MEDAVIS_RIS]: { displayName: 'Medavis RIS', operatingCountries: ['DE'] },
  [EHRProvider.Medical_Office]: {
    displayName: 'Medical Office',
    operatingCountries: ['DE'],
    supportsGdt: true,
    pvsSystemName: 'Medical Office',
    pvsGdtId: 'MEDOF01',
    requiresFixedFilename: true,
    defaultInputFilename: 'mo2heidi.gdt',
    defaultOutputFilename: 'heidi2mo.gdt',
    defaultGdtSectionMappings: getMedicalOfficeDefaultGdtSectionMappings(),
  },
  [EHRProvider.medico]: {
    displayName: 'medico',
    operatingCountries: ['DE'],
    supportsGdt: true,
    pvsSystemName: 'Other / Custom',
  },
  [EHRProvider.MEDYS]: {
    displayName: 'Medys',
    operatingCountries: ['DE'],
    supportsGdt: true,
    pvsSystemName: 'Other / Custom',
  },
  [EHRProvider.Nephro]: { displayName: 'medVISON NEPHRO', operatingCountries: ['DE'] },
  [EHRProvider.Nephro7]: {
    displayName: 'medVISON NEPHRO7',
    operatingCountries: ['DE'],
    supportsGdt: true,
    pvsSystemName: 'Nephro7',
    pvsGdtId: 'NEPHR01',
    requiresFixedFilename: true,
    defaultInputFilename: 'nephro2heidi.gdt',
    defaultOutputFilename: 'heidi2nephro.gdt',
  },
  [EHRProvider.NEXUS_KIS]: { displayName: 'Nexus KIS', operatingCountries: ['DE'] },
  [EHRProvider.ORBIS]: { displayName: 'Orbis', operatingCountries: ['DE'] },
  [EHRProvider.ORBIS_U]: { displayName: 'Orbis U', operatingCountries: ['DE'] },
  [EHRProvider.PegaMed]: {
    displayName: 'PegaMed',
    operatingCountries: ['DE'],
    supportsGdt: true,
    pvsSystemName: 'PegaMed',
    pvsGdtId: 'PEGAM01',
    requiresFixedFilename: true,
    defaultInputFilename: 'PegaHeidi.gdt',
    defaultOutputFilename: 'HeidiPega.gdt',
  },
  [EHRProvider.Praxis_Programm]: {
    displayName: 'Praxis-Programm',
    operatingCountries: ['DE'],
  },
  [EHRProvider.PROFIMED]: { displayName: 'Profimed', operatingCountries: ['DE'] },
  [EHRProvider.Pro_Medico]: { displayName: 'Pro Medico', operatingCountries: ['DE'] },
  [EHRProvider.PsychoDat]: { displayName: 'PsychoDat', operatingCountries: ['DE'] },
  [EHRProvider.psyprax]: {
    displayName: 'psyprax',
    operatingCountries: ['DE'],
    supportsGdt: true,
    pvsSystemName: 'Other / Custom',
  },
  [EHRProvider.PsyPrax_KIS]: { displayName: 'PsyPrax KIS', operatingCountries: ['DE'] },
  [EHRProvider.QUINCY_WIN]: { displayName: 'Quincy Win', operatingCountries: ['DE'] },
  [EHRProvider.RED_Medical]: { displayName: 'Red Medical', operatingCountries: ['DE'] },
  [EHRProvider.S3_Win]: { displayName: 'S3-Win', operatingCountries: ['DE'] },
  [EHRProvider.SAP_IS_H_i_s_h_med]: {
    displayName: 'SAP IS-H + i.s.h.med',
    operatingCountries: ['DE'],
  },
  [EHRProvider.SMARTY]: { displayName: 'Smarty', operatingCountries: ['DE'] },
  [EHRProvider.T2med]: {
    displayName: 'T2med',
    operatingCountries: ['DE'],
    supportsGdt: true,
    pvsSystemName: 'T2med',
    pvsGdtId: 'T2MED_PX',
    requiresFixedFilename: true,
    defaultInputFilename: 'HeidT2MD.gdt',
    defaultOutputFilename: 'T2MDHeid.gdt',
    // T2med uses 3-char eAkte category codes, not standard 2-char GDT codes
    defaultGdtSectionMappings: [
      { fieldId: '6330', sectionName: 'Anamnese', categoryCode: 'ANA' },
      { fieldId: '6330', sectionName: 'Befund', categoryCode: 'BEF' },
      { fieldId: '6330', sectionName: 'Therapie', categoryCode: 'THE' },
      { fieldId: '6330', sectionName: 'Procedere', categoryCode: 'PRO' },
    ],
  },
  [EHRProvider.tomedo]: {
    displayName: 'Tomedo',
    operatingCountries: ['DE'],
    supportsGdt: true,
    pvsSystemName: 'Tomedo',
    // Tomedo uses its own product name in lowercase as the GDT receiver ID (field 8315)
    pvsGdtId: 'tomedo',
    isMacOSOnly: true,
    requiresFixedFilename: true,
    defaultInputFilename: 'tomedoheidi.gdt',
    defaultOutputFilename: 'heiditomedo.gdt',
  },
  [EHRProvider.TURBOMED]: {
    displayName: 'Turbomed',
    operatingCountries: ['DE'],
    supportsGdt: true,
    pvsSystemName: 'Turbomed',
    pvsGdtId: 'TURBO01',
    requiresFixedFilename: true,
    defaultInputFilename: 'turbo2heidi.gdt',
    defaultOutputFilename: 'heidi2turbo.gdt',
  },
  [EHRProvider.vianova_KIS]: { displayName: 'vianova KIS', operatingCountries: ['DE'] },
  [EHRProvider.x_vianova]: {
    displayName: 'Medatixx x.vianova',
    operatingCountries: ['DE'],
    supportsGdt: true,
    pvsSystemName: 'Medatixx x.vianova',
    pvsGdtId: 'XVIAN01',
    requiresFixedFilename: true,
    defaultInputFilename: 'vianova2heidi.gdt',
    defaultOutputFilename: 'heidi2vianova.gdt',
    defaultGdtSectionMappings: getMedatixxDefaultGdtSectionMappings(),
    requiresSectionHeadingInContent: true,
  },
  [EHRProvider.x_comfort]: {
    displayName: 'Medatixx x.comfort',
    operatingCountries: ['DE'],
    supportsGdt: true,
    pvsSystemName: 'Medatixx x.comfort',
    pvsGdtId: 'XCOMF01',
    requiresFixedFilename: true,
    defaultInputFilename: 'comfort2heidi.gdt',
    defaultOutputFilename: 'heidi2comfort.gdt',
    defaultGdtSectionMappings: getMedatixxDefaultGdtSectionMappings(),
    requiresSectionHeadingInContent: true,
  },
  [EHRProvider.x_concept]: {
    displayName: 'Medatixx x.concept',
    operatingCountries: ['DE'],
    supportsGdt: true,
    pvsSystemName: 'Medatixx x.concept',
    pvsGdtId: 'XCONC01',
    requiresFixedFilename: true,
    defaultInputFilename: 'concept2heidi.gdt',
    defaultOutputFilename: 'heidi2concept.gdt',
    defaultGdtSectionMappings: getMedatixxDefaultGdtSectionMappings(),
    requiresSectionHeadingInContent: true,
  },
  [EHRProvider.x_isynet]: {
    displayName: 'Medatixx x.isynet',
    operatingCountries: ['DE'],
    supportsGdt: true,
    pvsSystemName: X_ISYNET_PVS_SYSTEM_NAME,
    pvsGdtId: 'XISYN01',
    requiresFixedFilename: true,
    defaultInputFilename: 'isy2heidi.gdt',
    defaultOutputFilename: 'heidi2isy.gdt',
    supportsGdtSectionalNotes: true,
    allowsGdtDirectFieldMapping: false,
    requiresGdtSectionalNotesTplSupport: true,
  },
  [EHRProvider.xentro]: {
    displayName: 'Medatixx xentro',
    operatingCountries: ['DE'],
    supportsGdt: true,
    pvsSystemName: 'Medatixx xentro',
    pvsGdtId: 'XENTR01',
    requiresFixedFilename: true,
    defaultInputFilename: 'xentro2heidi.gdt',
    defaultOutputFilename: 'heidi2xentro.gdt',
    defaultGdtSectionMappings: getMedatixxDefaultGdtSectionMappings(),
    requiresSectionHeadingInContent: true,
  },
  [EHRProvider.xyCare]: { displayName: 'xyCare', operatingCountries: ['DE'] },

  // ==================== Australian/NZ Systems ====================
  [EHRProvider.Best_Practice]: {
    displayName: 'Best Practice',
    operatingCountries: ['AU'],
  },
  [EHRProvider.Clinic_to_Cloud]: {
    displayName: 'Clinic to Cloud',
    operatingCountries: ['AU'],
  },
  [EHRProvider.Cliniko]: {
    displayName: 'Cliniko',
    operatingCountries: ['AU', 'NZ', 'GB', 'US', 'CA'],
  }, // Global but main markets
  [EHRProvider.Genie]: { displayName: 'Genie', operatingCountries: ['AU'] },
  [EHRProvider.Gensolve]: { displayName: 'Gensolve', operatingCountries: ['AU', 'NZ'] },
  [EHRProvider.Halaxy]: { displayName: 'Halaxy', operatingCountries: ['AU'] },
  [EHRProvider.Magentus]: { displayName: 'Magentus', operatingCountries: ['AU'] },
  [EHRProvider.Magentus_Genie_]: {
    displayName: 'Magentus Genie',
    operatingCountries: ['AU'],
  },
  [EHRProvider.Medical_Director]: {
    displayName: 'Medical Director',
    operatingCountries: ['AU'],
  },
  [EHRProvider.MediRecords]: { displayName: 'MediRecords', operatingCountries: ['AU'] },
  [EHRProvider.Medtech]: { displayName: 'Medtech', operatingCountries: ['NZ'] },
  [EHRProvider.Nookal]: { displayName: 'Nookal', operatingCountries: ['AU', 'NZ', 'GB'] },
  [EHRProvider.Power_Diary]: {
    displayName: 'Power Diary',
    operatingCountries: ['AU', 'NZ', 'GB'],
  },
  [EHRProvider.Pracsoft]: { displayName: 'Pracsoft', operatingCountries: ['AU'] },
  [EHRProvider.PracSuite]: { displayName: 'PracSuite', operatingCountries: ['AU'] },
  [EHRProvider.Splose]: { displayName: 'Splose', operatingCountries: ['AU'] },
  [EHRProvider.Timely]: { displayName: 'Timely', operatingCountries: ['NZ', 'AU', 'GB'] },
  [EHRProvider.Zedmed]: { displayName: 'Zedmed', operatingCountries: ['AU'] },
  [EHRProvider.Healthdirect]: { displayName: 'Healthdirect', operatingCountries: ['AU'] },

  // ==================== UK Systems ====================
  [EHRProvider.EMIS]: { displayName: 'EMIS', operatingCountries: ['GB'] },
  [EHRProvider.TPP_SystmOne]: { displayName: 'TPP SystmOne', operatingCountries: ['GB'] },
  [EHRProvider.Vision_Health]: {
    displayName: 'Vision Health',
    operatingCountries: ['GB'],
  },
  [EHRProvider.iaptus_by_Mayden]: {
    displayName: 'iaptus by Mayden',
    operatingCountries: ['GB'],
  },
  [EHRProvider.Pabau]: { displayName: 'Pabau', operatingCountries: ['GB', 'US'] },
  [EHRProvider.Semble]: { displayName: 'Semble', operatingCountries: ['GB'] },
  [EHRProvider.SGIMED]: { displayName: 'SGIMED', operatingCountries: ['CH'] },
  [EHRProvider.WriteUpp]: { displayName: 'WriteUpp', operatingCountries: ['GB', 'AU'] },

  // ==================== US Systems ====================
  [EHRProvider.Allscripts]: { displayName: 'Allscripts', operatingCountries: ['US'] },
  [EHRProvider.AmazingCharts]: {
    displayName: 'Amazing Charts',
    operatingCountries: ['US'],
  },
  [EHRProvider.Aprima]: { displayName: 'Aprima', operatingCountries: ['US'] },
  [EHRProvider.athenahealth]: { displayName: 'athenahealth', operatingCountries: ['US'] },
  [EHRProvider.CareCloud]: { displayName: 'CareCloud', operatingCountries: ['US'] },
  [EHRProvider.CareStack]: { displayName: 'CareStack', operatingCountries: ['US'] },
  [EHRProvider.Cerbo]: { displayName: 'Cerbo', operatingCountries: ['US'] },
  [EHRProvider.Cerner]: { displayName: 'Cerner', operatingCountries: ['US', 'GB', 'AU'] }, // International
  [EHRProvider.Charm]: { displayName: 'Charm', operatingCountries: ['US'] },
  [EHRProvider.ChartLogic]: { displayName: 'ChartLogic', operatingCountries: ['US'] },
  [EHRProvider.Compulink]: { displayName: 'Compulink', operatingCountries: ['US'] },
  [EHRProvider.CureMD]: { displayName: 'CureMD', operatingCountries: ['US'] },
  [EHRProvider.DrChrono]: { displayName: 'DrChrono', operatingCountries: ['US'] },
  [EHRProvider.eClinicalWorks]: {
    displayName: 'eClinicalWorks',
    operatingCountries: ['US'],
  },
  [EHRProvider.eClinicalWorks_EHR_Launch]: {
    displayName: 'eClinicalWorks EHR Launch',
    operatingCountries: ['US'],
  },
  [EHRProvider.Elation]: { displayName: 'Elation', operatingCountries: ['US'] },
  [EHRProvider.Epic_Systems]: {
    displayName: 'Epic',
    operatingCountries: ['US', 'AU', 'GB', 'NZ', 'CA', 'DE', 'AT', 'CH'],
  }, // Truly international
  [EHRProvider.Greenway_Health]: {
    displayName: 'Greenway Health',
    operatingCountries: ['US'],
  },
  [EHRProvider.Kareo]: { displayName: 'Kareo', operatingCountries: ['US'] },
  [EHRProvider.Meditech_Expanse]: {
    displayName: 'Meditech Expanse',
    operatingCountries: ['US', 'CA', 'AU'],
  },
  [EHRProvider.Modernizing_Medicine]: {
    displayName: 'Modernizing Medicine',
    operatingCountries: ['US'],
  },
  [EHRProvider.NextGen_Healthcare]: {
    displayName: 'NextGen Healthcare',
    operatingCountries: ['US'],
  },
  [EHRProvider.Practice_Fusion]: {
    displayName: 'Practice Fusion',
    operatingCountries: ['US'],
  },
  [EHRProvider.Prognocis]: { displayName: 'Prognocis', operatingCountries: ['US'] },
  [EHRProvider.RevolutionEHR]: {
    displayName: 'RevolutionEHR',
    operatingCountries: ['US'],
  },
  [EHRProvider.SimplePractice]: {
    displayName: 'SimplePractice',
    operatingCountries: ['US', 'CA'],
  },
  [EHRProvider.Veradigm_EHR]: { displayName: 'Veradigm', operatingCountries: ['US'] },
  [EHRProvider.WebPT]: { displayName: 'WebPT', operatingCountries: ['US'] },

  // ==================== Canadian Systems ====================
  [EHRProvider.JunoOscar]: { displayName: 'Juno/Oscar', operatingCountries: ['CA'] },
  [EHRProvider.Oscar_Pro]: { displayName: 'Oscar Pro', operatingCountries: ['CA'] },
  [EHRProvider.Telus]: { displayName: 'Telus Health', operatingCountries: ['CA'] },

  // ==================== Truly Global Systems (no specific countries) ====================
  [EHRProvider.IntakeQ]: { displayName: 'IntakeQ', operatingCountries: undefined }, // Global
  [EHRProvider.InterSystems]: {
    displayName: 'InterSystems',
    operatingCountries: undefined,
  }, // Global
  [EHRProvider.Mindbody]: { displayName: 'Mindbody', operatingCountries: undefined }, // Global
  [EHRProvider.Practice_Better]: {
    displayName: 'Practice Better',
    operatingCountries: undefined,
  }, // Global
  [EHRProvider.TheraNest]: { displayName: 'TheraNest', operatingCountries: undefined }, // Global
  [EHRProvider.TherapyNotes]: {
    displayName: 'TherapyNotes',
    operatingCountries: undefined,
  }, // Global

  // ==================== Other ====================
  [EHRProvider.Other]: { displayName: 'Other', operatingCountries: undefined },
};

/**
 * Get metadata for an EHR provider
 * Falls back to using the raw provider string if no metadata exists
 */
export function getEhrProviderMetadata(provider: EHRProvider): EhrProviderMetadata {
  const metadata = EHR_PROVIDER_METADATA[provider];
  if (metadata) {
    return metadata;
  }

  // Fallback: use the provider string as display name
  return {
    displayName: provider,
    operatingCountries: undefined,
  };
}

/**
 * Check if a provider operates in a specific country
 * Note: Truly global providers (no operatingCountries) return FALSE here
 * They are handled separately in sorting
 */
export function operatesInCountry(provider: EHRProvider, countryCode: string): boolean {
  const metadata = getEhrProviderMetadata(provider);
  // Truly global (no operating countries) - return false, they're sorted separately
  if (!metadata.operatingCountries || metadata.operatingCountries.length === 0) {
    return false;
  }
  return metadata.operatingCountries.includes(countryCode.toUpperCase());
}

/**
 * Check if a provider is truly global (no specific countries)
 */
export function isGlobalProvider(provider: EHRProvider): boolean {
  const metadata = getEhrProviderMetadata(provider);
  return !metadata.operatingCountries || metadata.operatingCountries.length === 0;
}

/**
 * Get display label for a provider's countries
 * - Single country: show flag
 * - 2 countries: show both flags
 * - 3+ countries: show 🌐 Global
 * - No countries (truly global): show 🌐 Global
 */
export function getCountryDisplay(provider: EHRProvider): string {
  const metadata = getEhrProviderMetadata(provider);

  if (!metadata.operatingCountries || metadata.operatingCountries.length === 0) {
    return '🌐'; // Truly global
  }

  if (metadata.operatingCountries.length <= 2) {
    return metadata.operatingCountries
      .map((code) => COUNTRY_FLAGS[code] || code)
      .join('');
  }

  return '🌐'; // 3+ countries = show as global
}

/**
 * Get the primary country for display grouping
 * Returns the first operating country, or 'Global' if none
 */
export function getPrimaryCountry(provider: EHRProvider): string {
  const metadata = getEhrProviderMetadata(provider);
  if (!metadata.operatingCountries || metadata.operatingCountries.length === 0) {
    return 'Global';
  }
  return metadata.operatingCountries[0] ?? 'Global';
}

/**
 * Returns the user-facing EHR name used in "Push to <name>" buttons for the
 * Magentus push-note/letter flow. Genie and Genie Web are the same EHR product
 * accessed via different integration types; Gentu is the Magentus-branded variant.
 */
export function getMagentusEhrDisplayName(provider: EHRProvider | undefined): string {
  if (provider === EHRProvider.Genie) return 'Genie';
  if (provider === EHRProvider.Genie_Web) return 'Genie Web';
  return 'Gentu';
}

/**
 * Returns the EHR provider to pass to <EhrIcon> in the Magentus push-note/letter
 * buttons. Falls back to Magentus when the provider is unknown or undefined.
 */
export function getMagentusEhrIconProvider(
  provider: EHRProvider | undefined
): EHRProvider {
  return provider ?? EHRProvider.Magentus;
}

/**
 * Check if a provider supports GDT integration.
 * Returns false if the GDT feature flag is disabled.
 *
 * T2med stops advertising GDT once the FHIR sectional write-back flag is on;
 * the legacy GDT file exchange remains the fallback while the flag is off.
 */
export function supportsGdt(provider: EHRProvider): boolean {
  if (!IS_GDT_INTEGRATION_ENABLED()) return false;
  if (
    provider === EHRProvider.T2med &&
    posthog.getFeatureFlag(T2MED_FEATURE_FLAG) === true
  ) {
    return false;
  }
  const metadata = EHR_PROVIDER_METADATA[provider];
  return metadata?.supportsGdt === true;
}

/**
 * Get all providers that support GDT.
 * Returns empty array if the GDT feature flag is disabled.
 */
export function getGdtSupportedProviders(): EHRProvider[] {
  if (!IS_GDT_INTEGRATION_ENABLED()) return [];
  return Object.entries(EHR_PROVIDER_METADATA)
    .filter(([, meta]) => meta.supportsGdt)
    .map(([provider]) => provider as EHRProvider);
}

/**
 * Get PVS system name for an EHR provider (for GDT auto-configuration)
 */
export function getPvsSystemName(provider: EHRProvider): string | undefined {
  const metadata = EHR_PROVIDER_METADATA[provider];
  return metadata?.pvsSystemName;
}

/**
 * Reverse lookup: find the EHR provider for a given PVS system name.
 * Returns the first matching provider with `supportsGdt: true`.
 */
export function getEhrProviderByPvsSystemName(
  pvsSystemName: string
): EHRProvider | undefined {
  if (!pvsSystemName) return undefined;

  // Match the Rust PVS strategies: saved configuration may contain different
  // casing or incidental whitespace, but must still resolve to the same profile.
  const normalised = pvsSystemName.toLowerCase().replace(/\s+/g, '');
  const match = Object.entries(EHR_PROVIDER_METADATA).find(
    ([, meta]) =>
      meta?.supportsGdt === true &&
      meta.pvsSystemName?.toLowerCase().replace(/\s+/g, '') === normalised
  );

  return match ? (match[0] as EHRProvider) : undefined;
}

/**
 * Get default PVS GDT-ID for an EHR provider
 */
export function getPvsGdtId(provider: EHRProvider): string | undefined {
  const metadata = EHR_PROVIDER_METADATA[provider];
  return metadata?.pvsGdtId;
}

/**
 * Check if an EHR provider's PVS requires a fixed output filename
 * (as opposed to unique timestamped filenames)
 */
export function requiresFixedFilename(provider: EHRProvider): boolean {
  const metadata = EHR_PROVIDER_METADATA[provider];
  return metadata?.requiresFixedFilename === true;
}

/**
 * Get the default input filename the PVS writes to
 */
export function getDefaultInputFilename(provider: EHRProvider): string | undefined {
  const metadata = EHR_PROVIDER_METADATA[provider];
  return metadata?.defaultInputFilename;
}

/**
 * Get the default output filename for an EHR provider that requires fixed filenames
 */
export function getDefaultOutputFilename(provider: EHRProvider): string | undefined {
  const metadata = EHR_PROVIDER_METADATA[provider];
  return metadata?.defaultOutputFilename;
}

/**
 * Get default GDT section-to-category mappings for an EHR provider.
 * Returns provider-specific overrides when defined, otherwise falls back to
 * STANDARD_GDT_SECTION_MAPPINGS for all GDT-capable providers so every
 * German practice gets Anamnese/Befund/Therapie/Procedere pre-populated.
 */
export function getDefaultGdtSectionMappings(
  provider: EHRProvider
): Array<{ sectionName: string; categoryCode: string; fieldId?: string }> | undefined {
  const metadata = EHR_PROVIDER_METADATA[provider];
  if (!metadata?.supportsGdt || !supportsGdtSectionalNotes(provider)) return undefined;
  return (metadata.defaultGdtSectionMappings ?? [...STANDARD_GDT_SECTION_MAPPINGS]).map(
    (entry) => ({ ...entry })
  );
}

/**
 * Check whether a GDT provider accepts Heidi's structured section fields.
 * Returns true by default so existing GDT providers retain their current behavior.
 */
export function supportsGdtSectionalNotes(provider: EHRProvider): boolean {
  const metadata = EHR_PROVIDER_METADATA[provider];
  return metadata?.supportsGdt === true && metadata.supportsGdtSectionalNotes !== false;
}

/**
 * Whether this PVS requires section headings written as the first 6331 content line.
 * Returns false by default — most PVS systems render section labels from the 6330 code.
 */
export function requiresSectionHeadingInContent(provider: EHRProvider): boolean {
  const metadata = EHR_PROVIDER_METADATA[provider];
  return metadata?.requiresSectionHeadingInContent === true;
}

/**
 * Whether this PVS reads section content by GDT field identifier (Duria's
 * `Feldkennung,Textgruppe` model) rather than by a 6330 letter code.
 * Returns false by default — only Duria/Duria2 opt in.
 */
export function gdtSectionsAsFieldIds(provider: EHRProvider): boolean {
  const metadata = EHR_PROVIDER_METADATA[provider];
  return metadata?.gdtSectionsAsFieldIds === true;
}

/**
 * Whether users may configure direct GDT field-id routing for note sections.
 * Defaults to true; providers whose importers require category/content pairs opt out.
 */
export function allowsGdtDirectFieldMapping(provider: EHRProvider): boolean {
  const metadata = EHR_PROVIDER_METADATA[provider];
  return metadata?.allowsGdtDirectFieldMapping !== false;
}

/**
 * Whether sectional notes require an external PVS template with paired-field support.
 */
export function requiresGdtSectionalNotesTplSupport(provider: EHRProvider): boolean {
  const metadata = EHR_PROVIDER_METADATA[provider];
  return metadata?.requiresGdtSectionalNotesTplSupport === true;
}

/**
 * Check if an EHR provider's PVS is macOS-only (e.g., tomedo)
 */
export function isMacOSOnlyProvider(provider: EHRProvider): boolean {
  const metadata = EHR_PROVIDER_METADATA[provider];
  return metadata?.isMacOSOnly === true;
}

/**
 * Advanced sorting for EHR providers based on user's country
 *
 * Sort order:
 * 1. Systems that operate in user's country (including multi-country systems like Epic)
 *    - Sorted alphabetically by display name
 *    - Example for AU user: Best Practice, Clinic to Cloud, Epic, Medical Director...
 *
 * 2. Systems from other specific countries (not in user's country)
 *    - Sorted alphabetically by country name, then by provider name within each country
 *    - Example: Austria systems, Canada systems, Germany systems, UK systems, US systems...
 *
 * 3. Truly global systems (no specific operating countries)
 *    - These go at the very end, sorted alphabetically
 *    - Example: IntakeQ, InterSystems, Mindbody, Practice Better...
 *
 * @param providers List of providers to sort
 * @param userCountryCode User's ISO 2-letter country code (e.g., 'AU', 'DE', 'US')
 */
export function sortEhrProvidersByUserCountry(
  providers: EHRProvider[],
  userCountryCode?: string
): EHRProvider[] {
  const userCountry = userCountryCode?.toUpperCase();

  return [...providers].sort((a, b) => {
    const metaA = getEhrProviderMetadata(a);
    const metaB = getEhrProviderMetadata(b);

    // Check if systems operate in user's country
    const aOperatesInUserCountry = userCountry && operatesInCountry(a, userCountry);
    const bOperatesInUserCountry = userCountry && operatesInCountry(b, userCountry);

    // Check if systems are truly global (no specific countries)
    const aIsTrulyGlobal = isGlobalProvider(a);
    const bIsTrulyGlobal = isGlobalProvider(b);

    // GROUP 1: Systems operating in user's country come first
    if (aOperatesInUserCountry && !bOperatesInUserCountry) return -1;
    if (bOperatesInUserCountry && !aOperatesInUserCountry) return 1;

    // Both operate in user's country - sort alphabetically
    if (aOperatesInUserCountry && bOperatesInUserCountry) {
      return metaA.displayName.localeCompare(metaB.displayName);
    }

    // GROUP 3: Truly global systems (no specific countries) go LAST
    if (aIsTrulyGlobal && !bIsTrulyGlobal) return 1;
    if (bIsTrulyGlobal && !aIsTrulyGlobal) return -1;

    // Both are truly global - sort alphabetically
    if (aIsTrulyGlobal && bIsTrulyGlobal) {
      return metaA.displayName.localeCompare(metaB.displayName);
    }

    // GROUP 2: Both are from specific countries (not user's)
    // Sort by country name, then by provider name
    const countryA = COUNTRY_NAMES[getPrimaryCountry(a)] || getPrimaryCountry(a);
    const countryB = COUNTRY_NAMES[getPrimaryCountry(b)] || getPrimaryCountry(b);

    const countryCompare = countryA.localeCompare(countryB);
    if (countryCompare !== 0) return countryCompare;

    return metaA.displayName.localeCompare(metaB.displayName);
  });
}

// Legacy export for backward compatibility
export type EhrRegion = 'dach' | 'anz' | 'uk' | 'us' | 'ca' | 'global' | 'other';

export function countryCodeToRegion(
  countryCode: string | undefined
): EhrRegion | undefined {
  if (!countryCode) return undefined;

  const code = countryCode.toUpperCase();

  if (['DE', 'AT', 'CH'].includes(code)) return 'dach';
  if (['AU', 'NZ'].includes(code)) return 'anz';
  if (['GB', 'UK'].includes(code)) return 'uk';
  if (code === 'US') return 'us';
  if (code === 'CA') return 'ca';

  return undefined;
}

// Keep old function for backward compatibility but use new sorting internally
export function sortEhrProvidersByRegion(
  providers: EHRProvider[],
  userRegion?: EhrRegion
): EHRProvider[] {
  // Map region to country code for new sorting
  let userCountryCode: string | undefined;
  if (userRegion === 'dach') userCountryCode = 'DE';
  else if (userRegion === 'anz') userCountryCode = 'AU';
  else if (userRegion === 'uk') userCountryCode = 'GB';
  else if (userRegion === 'us') userCountryCode = 'US';
  else if (userRegion === 'ca') userCountryCode = 'CA';

  return sortEhrProvidersByUserCountry(providers, userCountryCode);
}
